import EnumValueNotFoundException from '~/errors/EnumValueNotFoundException';

import { isPlainObject } from '@vestigas/utils';
import { log } from '~/utils/logging';
import { getAbbreviatedUnit } from '~/utils/unit';
import UnitUtils from '~/utils/unitUtils';

import { AttachmentObject } from '../deliveries/attachmentUtils';
import { type AttachmentTypeType } from '../deliveries/types';
import { SignatureRolesObject } from '../masterdata/SignatureRoles';

import { AcceptStateCalculatorObject } from './acceptStateCalculatorUtils';
import { ACCEPT_ARTICLE_ACCEPT_STATUS } from './constants';
import { type AcceptItem, type PartialAcceptStatus } from './types';

export const AcceptArticleObject = {
  ACCEPT_STATUS: ACCEPT_ARTICLE_ACCEPT_STATUS,
  /**
   * Creates a new AcceptArticleObject object.
   * @param acceptItem - The item data from the API.
   * @param chainId - The chain ID for attachments.
   */
  create(acceptItem: Partial<AcceptItem> = {}, chainId?: string) {
    const acceptStatus = acceptItem?.accept ?? undefined;
    const partialAcceptStatus = acceptItem?.partialAccept ?? undefined;
    const reason = acceptItem?.reason ?? undefined;
    const attachments =
      acceptItem?.attachments?.map((attachment) =>
        AttachmentObject.create({
          blob: attachment.blob,
          chainId,
          type: attachment.type as AttachmentTypeType,
        }),
      ) ?? [];
    const acceptState = this.getAcceptState(acceptStatus, partialAcceptStatus);
    const documentLogisticsPackagePosition =
      this.getDocumentLogisticsPackagePosition(acceptItem?.path);
    const documentLineItemPosition = this.getDocumentLineItemPosition(
      acceptItem?.path,
    );

    return {
      ...this,
      acceptState,
      acceptStatus,
      attachments,
      documentLineItemPosition,
      documentLogisticsPackagePosition,
      partialAcceptStatus,
      reason,
    };
  },
  /**
   * Retrieves the acceptance article for a specific party based on provided positions.
   * @param {AcceptItem[]} acceptItems - An array of acceptance items.
   * @param {string} party - The party for which to retrieve the acceptance article (e.g., "recipient").
   * @param {number} articleDocumentLogisticsPackagePosition - The position of the document log package.
   * @param {number} articleDocumentLineItemPosition - The position of the document line item.
   * @returns {AcceptArticle} - The acceptance article for the specified party and positions.
   */
  getAcceptArticleOfParty(
    acceptItems: Record<string, Array<Partial<AcceptItem>>>,
    party: string,
    articleDocumentLogisticsPackagePosition: number,
    articleDocumentLineItemPosition: number,
  ): ReturnType<typeof this.create> {
    if (party === SignatureRolesObject.SIGNATURE_ROLE.RECIPIENT.KEY) {
      return this.getAcceptArticleOfRecipient(
        acceptItems,
        articleDocumentLogisticsPackagePosition,
        articleDocumentLineItemPosition,
      );
    }

    const acceptArticles =
      acceptItems?.[party]?.map((acceptItem) => this.create(acceptItem)) ?? [];
    const acceptArticle = acceptArticles.find(
      (acceptArticle) =>
        Number(acceptArticle.documentLogisticsPackagePosition) ===
          articleDocumentLogisticsPackagePosition &&
        Number(acceptArticle.documentLineItemPosition) ===
          articleDocumentLineItemPosition,
    );

    return acceptArticle ?? this.create();
  },
  /**
   * Retrieves the acceptance article for the recipient based on provided positions.
   * Handles both legacy and current recipient articles.
   * @param {AcceptItem[]} acceptItems - An array of acceptance items.
   * @param {number} articleDocumentLogisticsPackagePosition - The position of the document log package.
   * @param {number} articleDocumentLineItemPosition - The position of the document line item.
   * @returns {AcceptArticle} - The acceptance article for the recipient at the specified positions.
   */
  getAcceptArticleOfRecipient(
    acceptItems: Record<string, Array<Partial<AcceptItem>>>,
    articleDocumentLogisticsPackagePosition: number,
    articleDocumentLineItemPosition: number,
  ): ReturnType<typeof this.create> {
    const acceptArticlesRecipientRaw =
      acceptItems?.[SignatureRolesObject.SIGNATURE_ROLE.RECIPIENT.KEY] ?? [];
    const acceptArticlesLegacyRecipientRaw =
      acceptItems?.[SignatureRolesObject.SIGNATURE_ROLE.RECIPIENT.LEGACY_KEY] ??
      [];

    // Calculate accept states without creating full objects - just extract the required data
    const acceptStateRecipient =
      AcceptStateCalculatorObject.calculateOverallAcceptStateFromArticles(
        acceptArticlesRecipientRaw.map((acceptItem) =>
          this.getAcceptState(acceptItem?.accept, acceptItem?.partialAccept),
        ),
      );
    const acceptStateLegacyRecipient =
      AcceptStateCalculatorObject.calculateOverallAcceptStateFromArticles(
        acceptArticlesLegacyRecipientRaw.map((acceptItem) =>
          this.getAcceptState(acceptItem?.accept, acceptItem?.partialAccept),
        ),
      );

    let acceptArticlesRaw: Array<Partial<AcceptItem>> = [];

    // If the accept state of recipient is open and the accept state of buyer isn't open,
    // we assume that this is a legacy dln where the recipient accept article has been written into buyer.
    // However, this assumption isn't 100% safe and can lead to wrong results, but it is currently the best approach to handle legacy dlns.
    if (
      acceptStateLegacyRecipient !==
        AcceptStateCalculatorObject.ACCEPT_STATE.OPEN &&
      acceptStateRecipient === AcceptStateCalculatorObject.ACCEPT_STATE.OPEN
    ) {
      acceptArticlesRaw = acceptArticlesLegacyRecipientRaw;
    } else {
      acceptArticlesRaw = acceptArticlesRecipientRaw;
    }

    // Find the specific item BEFORE creating the object
    const matchingAcceptArticle = acceptArticlesRaw.find((acceptItem) => {
      const itemLogisticsPackagePosition = Number(
        this.getDocumentLogisticsPackagePosition(acceptItem?.path),
      );
      const itemLineItemPosition = Number(
        this.getDocumentLineItemPosition(acceptItem?.path),
      );

      return (
        itemLogisticsPackagePosition ===
          articleDocumentLogisticsPackagePosition &&
        itemLineItemPosition === articleDocumentLineItemPosition
      );
    });

    return matchingAcceptArticle
      ? this.create(matchingAcceptArticle)
      : this.create();
  },
  /**
   * Processes accept articles for all parties in a single pass for better performance.
   * This method replicates the exact logic of multiple getAcceptArticleOfParty calls
   * but processes all parties together with a single pass through the acceptItems.
   *
   * @param {Record<string, Array<Partial<AcceptItem>>>} acceptItems - An array of acceptance items.
   * @param {number} articleDocumentLogisticsPackagePosition - The position of the document log package.
   * @param {number} articleDocumentLineItemPosition - The position of the document line item.
   * @returns {Object} - Object containing accept articles for all parties.
   */
  getAcceptArticlesForAllParties(
    acceptItems: Record<string, Array<Partial<AcceptItem>>>,
    articleDocumentLogisticsPackagePosition: number,
    articleDocumentLineItemPosition: number,
  ) {
    const roles = SignatureRolesObject.SIGNATURE_ROLE;
    const result: Record<string, any> = {};

    result.supplier = this.create();
    result.carrier = this.create();
    result.onBehalfSupplier = this.create();
    result.onBehalfCarrier = this.create();
    result.onBehalfRecipient = this.create();

    // Process recipient using existing method (special case due to legacy handling)
    result.recipient = this.getAcceptArticleOfRecipient(
      acceptItems,
      articleDocumentLogisticsPackagePosition,
      articleDocumentLineItemPosition,
    );

    // Single pass through acceptItems to process all parties at once
    if (acceptItems) {
      for (const partyKey of Object.keys(acceptItems)) {
        const partyItems = acceptItems[partyKey];

        if (!partyItems || !Array.isArray(partyItems)) continue;

        // Process all items for this party and find the matching one
        for (const acceptItem of partyItems) {
          // Extract positions directly from raw data (no object creation needed)
          const itemLogisticsPackagePosition = Number(
            this.getDocumentLogisticsPackagePosition(acceptItem?.path),
          );
          const itemLineItemPosition = Number(
            this.getDocumentLineItemPosition(acceptItem?.path),
          );

          // Check if this item matches our position criteria BEFORE creating the expensive object
          if (
            itemLogisticsPackagePosition ===
              articleDocumentLogisticsPackagePosition &&
            itemLineItemPosition === articleDocumentLineItemPosition
          ) {
            // Only create the object when we know it matches!
            const createdArticle = this.create(acceptItem);
            // Map party key to result property
            switch (partyKey) {
              case roles.SUPPLIER.KEY: {
                result.supplier = createdArticle;
                break;
              }

              case roles.CARRIER.KEY: {
                result.carrier = createdArticle;
                break;
              }

              case roles.ON_BEHALF_SUPPLIER.KEY: {
                result.onBehalfSupplier = createdArticle;
                break;
              }

              case roles.ON_BEHALF_CARRIER.KEY: {
                result.onBehalfCarrier = createdArticle;
                break;
              }

              case roles.ON_BEHALF_RECIPIENT.KEY: {
                result.onBehalfRecipient = createdArticle;
                break;
              }

              // Note: RECIPIENT is handled separately above due to legacy logic
              default: {
                if (
                  partyKey !== roles.RECIPIENT.KEY &&
                  partyKey !== roles.RECIPIENT.LEGACY_KEY
                ) {
                  log.error({
                    message: `Party key ${partyKey} is not a valid party key`,
                    error: new Error(
                      `Party key ${partyKey} is not a valid party key`,
                    ),
                  });
                }

                break;
              }
            }

            break; // Found the matching article for this party, no need to continue
          }
        }
      }
    }

    return result;
  },
  /**
   * Determines the accept state based on accept and partial accept status.
   * @param acceptStatus - The overall accept status.
   * @param partialAcceptStatus - The partial accept status.
   */
  getAcceptState(
    acceptStatus: string | undefined,
    partialAcceptStatus: PartialAcceptStatus,
  ) {
    if (acceptStatus) {
      return acceptStatus === this.ACCEPT_STATUS.VALID.KEY
        ? AcceptStateCalculatorObject.ACCEPT_STATE.APPROVED
        : AcceptStateCalculatorObject.ACCEPT_STATE.DECLINED;
    }

    if (!partialAcceptStatus) {
      return AcceptStateCalculatorObject.ACCEPT_STATE.OPEN;
    }

    return AcceptStateCalculatorObject.ACCEPT_STATE.DECLINED;
  },
  /**
   * Retrieves the descriptive name of the current accept status.
   * @returns {string} - The descriptive name corresponding to the current accept status.
   * @throws {EnumValueNotFoundException} - If the accept status is invalid or not found in the constants.
   */
  getDescriptiveAcceptStatus(acceptStatus: string) {
    const acceptStatusConst = Object.keys(this.ACCEPT_STATUS).find(
      (x) =>
        this.ACCEPT_STATUS[x as keyof typeof this.ACCEPT_STATUS].KEY ===
        acceptStatus,
    );

    if (!acceptStatusConst) {
      throw new EnumValueNotFoundException(
        `Invalid accept status: ${acceptStatus}`,
      );
    }

    return this.ACCEPT_STATUS[
      acceptStatusConst as keyof typeof this.ACCEPT_STATUS
    ].DESCRIPTION;
  },
  /**
   * Extracts the document line item position from a path array.
   * @param path - The path array.
   */
  getDocumentLineItemPosition(path?: string[]) {
    if (!path) {
      return null;
    }

    // ATTENTION: This method used to translate all the path elements to camelCase.
    // This is taking a lot of performance, when there are many articles in the list, JUST to find the index of the logisticsPackage.
    // better to search the snake_case version of the path.
    const index = path.indexOf('line_item');

    return path[index + 1] ?? null;
  },
  /**
   * Extracts the document log package position from a path array.
   * @param path - The path array.
   */
  getDocumentLogisticsPackagePosition(path?: string[]) {
    if (!path) {
      return null;
    }

    // ATTENTION: This method used to translate all the path elements to camelCase.
    // This is taking a lot of performance, when there are many articles in the list, JUST to find the index of the logisticsPackage.
    // better to search the snake_case version of the path.
    const index = path.indexOf('logistics_package');

    return path[index + 1] ?? null;
  },
  /**
   * Converts partial accept statuses to a descriptive string.
   * @param partialAcceptStatus - The partial accept statuses.
   * @param unit - The unit for formatting values.
   */

  partialAcceptStatusToString(
    partialAcceptStatus: PartialAcceptStatus,
    unit: string | undefined,
  ) {
    if (!partialAcceptStatus || !isPlainObject(partialAcceptStatus)) {
      return '';
    }

    const items: string[] = [];
    for (const key of Object.keys(partialAcceptStatus)) {
      if (key === this.ACCEPT_STATUS.VALID.KEY) {
        continue;
      }

      const value = partialAcceptStatus[key];
      if (key === this.ACCEPT_STATUS.DELTA.KEY) {
        if (value < 0) {
          items.push(
            `${UnitUtils.formatValueUnitPairAsString_safe(
              -1 * value,
              unit,
              getAbbreviatedUnit,
            )} überschüssig`,
          );
        } else {
          items.push(
            `${UnitUtils.formatValueUnitPairAsString_safe(
              value,
              unit,
              getAbbreviatedUnit,
            )} fehlt`,
          );
        }

        continue;
      }

      const status = Object.keys(this.ACCEPT_STATUS).find(
        (statusKey) => key === this.ACCEPT_STATUS[statusKey].KEY,
      );
      if (!status) {
        log.error({
          message: `Invalid partial accept status: ${key}`,
          error: new EnumValueNotFoundException(
            `Invalid partial accept status: ${key}`,
          ),
        });

        continue;
      }

      items.push(
        `${UnitUtils.formatValueUnitPairAsString_safe(value, unit, getAbbreviatedUnit)} ${this.ACCEPT_STATUS[status].DESCRIPTION}`,
      );
    }

    return items.join(', ');
  },
  /**
   * Sets the unit for the object.
   * @param {string} unit - The unit to be set.
   */
  setUnit(unit: string | undefined) {
    this.unit = unit;
  },
  unit: undefined as string | undefined,
};
